import { Link, useLocation } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Markdown } from "@/components/site/Markdown";
import { Reveal } from "@/components/site/Reveal";
import { PortalButton } from "@/components/site/Header";
import { PHONE, PHONE_HREF, EMAIL } from "@/lib/api";
import NotFound from "./NotFound";
import { PageNotice } from "@/components/site/PageNotice";
import { ArrowUpRight } from "lucide-react";

export function Aside() {
  return (
    <aside className="space-y-6 lg:sticky lg:top-28">
      <div className="panel chamfer p-7">
        <p className="eyebrow-bronze">Talk to us</p>
        <p className="mt-3 font-display text-2xl uppercase">Not sure which tool?</p>
        <p className="mt-2 text-sm text-slate-400">Tell us the vehicles you work on and we'll recommend a setup.</p>
        <a href={PHONE_HREF} className="mt-5 flex items-center gap-3 font-mono text-lg text-[color:var(--ice)]" data-testid="aside-phone"><Phone className="h-4 w-4" />{PHONE}</a>
        <a href={`mailto:${EMAIL}`} className="mt-2 flex items-center gap-3 text-sm text-slate-300 hover:text-white"><Mail className="h-4 w-4" />{EMAIL}</a>
        <Link to="/contact" className="btn-ghost mt-6 w-full" data-testid="aside-contact-link">Send an enquiry</Link>
      </div>
      <div className="panel p-7">
        <p className="eyebrow">Existing dealers</p>
        <p className="mt-2 text-sm text-slate-400">Files, credits and uploads.</p>
        <PortalButton className="mt-4" testId="aside-portal-button" />
      </div>
    </aside>
  );
}

export default function ContentPage({ pageKey, extra = null }) {
  const { pathname } = useLocation();
  const { data: page, error } = useFetch(`/pages/${pageKey}`);
  if (error) return <NotFound />;
  return (
    <div data-testid={`content-page-${pageKey}`}>
      <Seo path={pathname} />
      <PageNotice page={page} />
      <PageHero page={page} crumbs={[["/", "Home"], [null, page?.title]]}>
        {page?.ctas?.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-3">
            {page.ctas.map((c, i) => c.url?.startsWith("http")
              ? <a key={c.url} href={c.url} target="_blank" rel="noopener" className={i ? "btn-ghost" : "btn-ice"} data-testid={`page-cta-${i}`}>{c.label} <ArrowUpRight className="h-4 w-4" /></a>
              : <Link key={c.url} to={c.url} className={i ? "btn-ghost" : "btn-ice"} data-testid={`page-cta-${i}`}>{c.label}</Link>)}
          </div>
        )}
      </PageHero>
      <section className="container-x grid gap-16 py-20 lg:grid-cols-[1fr_340px]">
        <Reveal><Markdown testId="page-body">{page?.body}</Markdown>{extra}</Reveal>
        <Aside />
      </section>
    </div>
  );
}
